import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Policy, Incident } from '../types/policy';

interface PolicyContextType {
  policies: Policy[];
  addPolicy: (policy: Policy) => void;
  getPolicy: (policyNumber: string) => Policy | undefined;
  cancelPolicy: (policyNumber: string, reason: string) => void;
  reinstatePolicy: (policyNumber: string) => void;
  renewPolicy: (policyNumber: string, newEndDate: string, newPremium: number) => void;
  addIncident: (policyNumber: string, incident: Incident) => void;
}

const PolicyContext = createContext<PolicyContextType | undefined>(undefined);

export const PolicyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [policies, setPolicies] = useState<Policy[]>([]);

  const addPolicy = (policy: Policy) => {
    setPolicies([...policies, policy]);
  };

  const getPolicy = (policyNumber: string): Policy | undefined => {
    return policies.find(p => p.policyNumber === policyNumber);
  };

  const cancelPolicy = (policyNumber: string, reason: string) => {
    setPolicies(policies.map(p => 
      p.policyNumber === policyNumber 
        ? { ...p, status: 'cancelled' as const, cancellationDate: new Date().toISOString(), cancellationReason: reason }
        : p
    ));
  };

  const reinstatePolicy = (policyNumber: string) => {
    setPolicies(policies.map(p => 
      p.policyNumber === policyNumber 
        ? { ...p, status: 'active' as const, cancellationDate: undefined, cancellationReason: undefined }
        : p
    ));
  };

  const renewPolicy = (policyNumber: string, newEndDate: string, newPremium: number) => {
    setPolicies(policies.map(p => 
      p.policyNumber === policyNumber 
        ? { 
            ...p, 
            endDate: newEndDate, 
            premiumAmount: newPremium,
            status: 'active' as const 
          }
        : p
    ));
  };

  const addIncident = (policyNumber: string, incident: Incident) => {
    setPolicies(policies.map(p => 
      p.policyNumber === policyNumber 
        ? { 
            ...p, 
            incidents: [...p.incidents, incident],
            coverageAmount: p.coverageAmount + incident.claimAmount
          }
        : p
    ));
  };

  return (
    <PolicyContext.Provider value={{
      policies,
      addPolicy,
      getPolicy,
      cancelPolicy,
      reinstatePolicy,
      renewPolicy,
      addIncident,
    }}>
      {children}
    </PolicyContext.Provider>
  );
};

export const usePolicy = () => {
  const context = useContext(PolicyContext);
  if (!context) {
    throw new Error('usePolicy must be used within a PolicyProvider');
  }
  return context;
};


